import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { environment } from '../../environments/environment';
import { AvailabilityBlocksComponent } from './availability-blocks/availability-blocks.component';

interface ResourceType {
  id: string | number;
  name: string;
  attributes?: ResourceAttribute[];
  capacity?: number;
  [key: string]: any;
}

interface ResourceAttribute {
  id: string;
  name: string;
  slug: string | null;
  type: string;
  resourceTypeId: string;
  order: number | null;
  selectDisplayValues?: string | null;
  selectSlugValues?: string | null;
}

interface ResourceAttributeValue {
  resourceAttributeId: string;
  resourceId: string;
  value: string;
}

interface Availability {
  id?: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

@Component({
  selector: 'app-copy-resource-dialog',
  templateUrl: './copy-resource-dialog.component.html',
  styleUrls: ['./copy-resource-dialog.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    AvailabilityBlocksComponent,
  ],
})
export class CopyResourceDialogComponent implements OnInit {
  attributeValues: { [key: string]: string } = {};
  selectOptionsCache: { [attributeId: string]: { slug: string; display: string }[] } = {};
  copyAvailability = true;
  sourceAvailability: Availability[] = [];
  loadingAvailability = false;
  
  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';
  
  constructor(
    public dialogRef: MatDialogRef<CopyResourceDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      id?: string;
      name: string;
      capacity?: number;
      resourceTypeId: string;
      resourceTypes: ResourceType[];
      resourceType?: ResourceType;
      attributeValues?: ResourceAttributeValue[];
      sourceResourceId: string;
      [key: string]: any;
    },
    private http: HttpClient,
    private snackBar: MatSnackBar
  ) {}
  
  ngOnInit() {
    // Get organization ID
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (storedId) {
      this.organisationId = storedId;
    }
    
    // Append " (Copy)" to the name
    this.data.name = `${this.data.name} (Copy)`;
    
    // Initialize capacity if not set
    if (!this.data.capacity) {
      this.data.capacity = 1;
    }
    
    // Set max capacity based on resource type's maxConcurrent
    this.updateCapacityConstraints();
    
    // Initialize attribute values from existing data
    if (this.data.attributeValues && this.data.attributeValues.length > 0) {
      this.data.attributeValues.forEach(attrValue => {
        this.attributeValues[attrValue.resourceAttributeId] = attrValue.value;
      });
    } else {
      // Initialize empty values for all attributes
      if (this.data.resourceType && this.data.resourceType.attributes) {
        this.data.resourceType.attributes.forEach(attr => {
          if (attr.type === 'checkbox') {
            this.attributeValues[attr.id] = 'false';
          } else if (attr.type === 'number') {
            this.attributeValues[attr.id] = '0';
          } else {
            this.attributeValues[attr.id] = '';
          }
        });
      }
    }

    // Precompute select options for all select attributes
    const attributes = this.getResourceTypeAttributes();
    attributes.forEach(attr => {
      if (attr.type === 'select') {
        this.selectOptionsCache[attr.id] = this.computeSelectOptions(attr);
      }
    });
    
    // Load source resource availability
    this.loadSourceAvailability();
  }
  
  loadSourceAvailability() {
    if (!this.data.sourceResourceId) return;
    
    this.loadingAvailability = true;
    this.http.get<Availability[]>(
      `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${this.data.resourceTypeId}/resources/${this.data.sourceResourceId}/availabilities`
    ).subscribe({
      next: (availability) => {
        this.sourceAvailability = availability || [];
        this.loadingAvailability = false;
      },
      error: (err) => {
        console.error('Failed to load availability:', err);
        this.sourceAvailability = [];
        this.loadingAvailability = false;
      }
    });
  }
  
  getResourceTypeName(): string {
    if (!this.data.resourceTypeId) return '';
    
    const resourceTypeId = this.data.resourceTypeId.toString();
    const resourceType = this.data.resourceTypes.find(rt => rt.id.toString() === resourceTypeId);
    return resourceType ? resourceType.name : '';
  }
  
  resourceTypeHasAttributes(): boolean {
    return this.getResourceTypeAttributes().length > 0;
  }
  
  getResourceTypeAttributes(): ResourceAttribute[] {
    if (!this.data.resourceTypeId) return [];
    
    const resourceTypeId = this.data.resourceTypeId.toString();
    const resourceType = this.data.resourceTypes.find(rt => rt.id.toString() === resourceTypeId);
    
    // Return attributes sorted by order
    if (resourceType && resourceType.attributes) {
      return [...resourceType.attributes].sort((a, b) => (a.order || 0) - (b.order || 0));
    }
    return [];
  }

  updateCapacityConstraints() {
    const resourceType = this.getResourceType();
    if (resourceType && resourceType['maxConcurrent'] !== null && resourceType['maxConcurrent'] !== undefined) {
      // If capacity exceeds maxConcurrent, reset it
      if (this.data.capacity && this.data.capacity > resourceType['maxConcurrent']) {
        this.data.capacity = resourceType['maxConcurrent'];
      }
    }
  }
  
  getResourceType(): ResourceType | undefined {
    if (!this.data.resourceTypeId) return undefined;
    const resourceTypeId = this.data.resourceTypeId.toString();
    return this.data.resourceTypes.find(rt => rt.id.toString() === resourceTypeId);
  }
  
  getMaxCapacity(): number | null {
    const resourceType = this.getResourceType();
    return resourceType?.['maxConcurrent'] || null;
  }
  
  getDayName(dayOfWeek: number): string {
    const dayNames = ['', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    return dayNames[dayOfWeek] || '';
  }

  onSubmit() {
    if (this.data.name && this.data.resourceTypeId) {
      // Validate capacity against resource type's maxConcurrent
      const resourceType = this.getResourceType();
      if (resourceType && resourceType['maxConcurrent'] !== null && resourceType['maxConcurrent'] !== undefined) {
        if (this.data.capacity && this.data.capacity > resourceType['maxConcurrent']) {
          return; // Don't submit if validation fails
        }
      }
      
      // Convert attribute values to array format
      const attributeValues: ResourceAttributeValue[] = [];
      
      for (const [attributeId, value] of Object.entries(this.attributeValues)) {
        attributeValues.push({
          resourceAttributeId: attributeId,
          resourceId: this.data.id || '',
          value: value.toString()
        });
      }
      
      this.dialogRef.close({
        ...this.data,
        attributeValues,
        copyAvailability: this.copyAvailability,
        sourceAvailability: this.copyAvailability ? this.sourceAvailability : []
      });
    }
  }

  /**
   * For select attributes, return an array of {slug, display} options.
   * This is now cached in selectOptionsCache.
   */
  getSelectOptions(attribute: ResourceAttribute): { slug: string; display: string }[] {
    return this.selectOptionsCache[attribute.id] || [];
  }

  private computeSelectOptions(attribute: ResourceAttribute): { slug: string; display: string }[] {
    if (!attribute || attribute.type !== 'select') return [];
    if (!attribute.selectSlugValues) return [];

    const displayArr = attribute.selectDisplayValues
      ? attribute.selectDisplayValues.split(',').map(s => s.trim())
      : [];
    const slugArr = attribute.selectSlugValues.split(',').map(s => s.trim());

    return slugArr.map((slug, idx) => ({
      slug,
      display: displayArr[idx] || slug
    }));
  }

  onCancel() {
    this.dialogRef.close();
  }
}